import { Component, Input, Output, EventEmitter } from '@angular/core';
import { FormsModule, ReactiveFormsModule, FormControl } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NPSColor, NPSRepresentation, QuestionSubType, QuestionType } from '@app/core/enums/questionnaire.enums';
import { Question } from '@app/core/models/questionnaire.model';
import { Textarea } from '@app/shared/components/form-fields/textarea/textarea';

@Component({
  selector: 'app-nps',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, Textarea],
  templateUrl: './nps.html',
  styleUrl: './nps.scss'
})
export class Nps {
  @Input() question: Question | null = null;
  @Input() page: 'theme' | 'preview' = 'preview';
  @Input() viewMode: 'mobile' | 'desktop' = 'desktop';
  @Output() scoreSelected = new EventEmitter<number>();
  @Output() followUpResponse = new EventEmitter<any>();
  @Input() data: any | null = null;
  
  questionSubType = QuestionSubType;
  questionType = QuestionType;
  npsRepresentation = NPSRepresentation;
  npsColor = NPSColor;
  
  selectedScore: number | null = null;
  hoveredScore: number | null = null;
  npsScale: number[] = Array.from({ length: 11 }, (_, i) => i); // 0-10 scale
  currentFollowUpQuestions: any[] = [];
  followUpResponseControls: Map<string, FormControl> = new Map();
  followUpSelectedOptions: Map<string, string[]> = new Map();
  followUpOthersTextMap: Map<string, string> = new Map();
  
  toNumber(value: any): number {
    const num = Number(value);
    return isNaN(num) ? 0 : num;
  }
  
  onScoreSelect(score: number): void {
    this.selectedScore = score;
    this.scoreSelected.emit(score);
    
    // Check for follow-up questions
    if (this.question?.followUpQuestionFlag && this.question?.followUpQuestions) {
      const category = this.getScoreCategory(score);
      // Filter all follow-up questions matching the category
      this.currentFollowUpQuestions = this.question.followUpQuestions.filter(
        (fuq: any) => fuq.questionCondition === category
      );
      
      // Initialize form controls and selected options for each follow-up question
      this.followUpResponseControls.clear();
      this.followUpSelectedOptions.clear();
      
      this.currentFollowUpQuestions.forEach((fuq: any) => {
        const questionId = fuq.followUpQuestionId || fuq.questionId;
        this.followUpResponseControls.set(questionId, new FormControl(''));
        this.followUpSelectedOptions.set(questionId, []);
      });
      this.followUpOthersTextMap.clear();
      
      // Emit initial response with score
      this.followUpResponse.emit({
        questionId: this.question?.questionId,
        score: score,
        followUpResponses: this.currentFollowUpQuestions.length > 0 ? [] : undefined,
      });
    } else {
      this.currentFollowUpQuestions = [];
      this.followUpResponseControls.clear();
      this.followUpSelectedOptions.clear();
      this.followUpOthersTextMap.clear();
      // Emit response without follow-up
      this.followUpResponse.emit({
        questionId: this.question?.questionId,
        score: score,
      });
    }
  }
  
  getScoreCategory(score: number): string {
    if (score <= 6) return 'DETRACTORS';
    if (score <= 8) return 'PASSIVES';
    return 'PROMOTERS';
  }
  
  onScoreHover(score: number | null): void {
    this.hoveredScore = score;
  }
  
  getScoreLabel(score: number): string {
    if (score <= 6) return 'Detractor';
    if (score <= 8) return 'Passive';
    return 'Promoter';
  }
  
  getScoreClass(score: number): string {
    if (this.selectedScore === score) return 'selected';
    if (score <= 6) return 'detractor';
    if (score <= 8) return 'passive';
    return 'promoter';
  }

  getScoreIndex(score: number): string {
    return `score-${score}`;
  }

  isLessThanSelected(score: number): boolean {
    return this.selectedScore !== null && score < this.selectedScore;
  }

  isLessThanHovered(score: number): boolean {
    return this.hoveredScore !== null && score < this.hoveredScore;
  }
  
  // Follow-up question handlers
  onFollowUpTextChange(questionId: string, value: string): void {
    if (!this.question) return;
    
    const allFollowUpResponses = this.buildAllFollowUpResponses();
    const existingIndex = allFollowUpResponses.findIndex((r: any) => r.questionId === questionId);
    
    const response = {
      questionId: questionId,
      textResponse: value,
      options: [],
      score: null,
    };
    
    if (existingIndex > -1) {
      allFollowUpResponses[existingIndex] = response;
    } else {
      allFollowUpResponses.push(response);
    }
    
    this.followUpResponse.emit({
      questionId: this.question.questionId,
      score: this.selectedScore,
      followUpResponses: allFollowUpResponses,
    });
  }
  
  onFollowUpOptionSelect(questionId: string, optionId: string, isMultiple: boolean = true): void {
    if (!this.question) return;
    
    const selectedOptions = this.followUpSelectedOptions.get(questionId) || [];
    
    if (isMultiple) {
      const index = selectedOptions.indexOf(optionId);
      if (index > -1) {
        selectedOptions.splice(index, 1);
      } else {
        selectedOptions.push(optionId);
      }
    } else {
      selectedOptions.length = 0;
      selectedOptions.push(optionId);
    }
    
    this.followUpSelectedOptions.set(questionId, selectedOptions);

    const fuq = this.currentFollowUpQuestions.find(
      (f: any) => (f.followUpQuestionId || f.questionId) === questionId
    );
    const othersOption = fuq ? this.getFollowUpOthersOption(fuq) : null;
    const othersOptionId = othersOption ? (othersOption.optionId || othersOption.text) : null;
    if (othersOptionId != null && !selectedOptions.includes(othersOptionId)) {
      this.followUpOthersTextMap.delete(questionId);
    }

    const allFollowUpResponses = this.buildAllFollowUpResponses();
    const existingIndex = allFollowUpResponses.findIndex((r: any) => r.questionId === questionId);
    const othersId = othersOptionId;
    const othersSelected = othersId != null && selectedOptions.includes(othersId);
    const othersResponse = othersSelected ? (this.followUpOthersTextMap.get(questionId) || '') : undefined;

    const response: any = {
      questionId: questionId,
      textResponse: null,
      options: selectedOptions.map((optId: string) => ({
        optionId: optId,
        optionSelected: 'true'
      })),
      score: null,
    };
    if (othersResponse !== undefined) {
      response.othersResponse = othersResponse;
    }

    if (existingIndex > -1) {
      allFollowUpResponses[existingIndex] = response;
    } else {
      allFollowUpResponses.push(response);
    }

    this.followUpResponse.emit({
      questionId: this.question.questionId,
      score: this.selectedScore,
      followUpResponses: allFollowUpResponses,
    });
  }
  
  isOptionSelected(questionId: string, optionId: string): boolean {
    

    const selectedOptions = this.followUpSelectedOptions.get(questionId) || [];
    return selectedOptions.includes(optionId);
  }
  
  getFollowUpQuestionOptions(followUpQuestion: any): any[] {
    if (!followUpQuestion) return [];
    return followUpQuestion.followUpQuestionOptions ||
           followUpQuestion.questionOptions ||
           [];
  }

  /** Options to display for choice-based follow-up, excluding "Others" */
  getFollowUpDisplayOptions(followUpQuestion: any): any[] {
    return this.getFollowUpQuestionOptions(followUpQuestion).filter(
      (opt: any) => !this.isFollowUpOthersOption(opt)
    );
  }

  /** The "Others" option for a follow-up if it exists */
  getFollowUpOthersOption(followUpQuestion: any): any | null {
    const options = this.getFollowUpQuestionOptions(followUpQuestion);
    return options.find((opt: any) => this.isFollowUpOthersOption(opt)) || null;
  }

  isFollowUpOthersOption(option: any): boolean {
    return option?.text?.toLowerCase().trim() === 'others';
  }

  isFollowUpOthersSelected(questionId: string, followUpQuestion: any): boolean {
    const othersOption = this.getFollowUpOthersOption(followUpQuestion);
    if (!othersOption) return false;
    const othersId = othersOption.optionId || othersOption.text;
    const selected = this.followUpSelectedOptions.get(questionId) || [];
    return selected.includes(othersId);
  }

  getFollowUpOthersText(questionId: string): string {
    return this.followUpOthersTextMap.get(questionId) || '';
  }

  onFollowUpOthersTextChange(questionId: string, text: string): void {
    if (!this.question) return;
    this.followUpOthersTextMap.set(questionId, text);
    const allFollowUpResponses = this.buildAllFollowUpResponses();
    const existingIndex = allFollowUpResponses.findIndex((r: any) => r.questionId === questionId);
    const selectedOptions = this.followUpSelectedOptions.get(questionId) || [];
    const response = {
      questionId: questionId,
      textResponse: null,
      options: selectedOptions.map(optId => ({
        optionId: optId,
        optionSelected: 'true'
      })),
      othersResponse: text,
      score: null,
    };
    if (existingIndex > -1) {
      allFollowUpResponses[existingIndex] = response;
    } else {
      allFollowUpResponses.push(response);
    }
    this.followUpResponse.emit({
      questionId: this.question.questionId,
      score: this.selectedScore,
      followUpResponses: allFollowUpResponses,
    });
  }

  getFollowUpResponseControl(questionId: string): FormControl {
    let control = this.followUpResponseControls.get(questionId);
    if (!control) {
      control = new FormControl('');
      this.followUpResponseControls.set(questionId, control);
    }
    return control;
  }
  
  private buildAllFollowUpResponses(): any[] {
    const responses: any[] = [];
    
    this.currentFollowUpQuestions.forEach((fuq: any) => {
      const questionId = fuq.followUpQuestionId || fuq.questionId;
      const control = this.followUpResponseControls.get(questionId);
      const selectedOptions = this.followUpSelectedOptions.get(questionId) || [];
      
      // Check if it's a text input question
      const isTextInput = fuq.followUpQuestionType === this.questionType.TEXT_INPUT ||
                         fuq.questionType === this.questionType.TEXT_INPUT ||
                         fuq.questionType === 'TEXT_INPUT' ||
                         fuq.questionType === 'TEXT';
      
      if (isTextInput && control?.value) {
        responses.push({
          questionId: questionId,
          textResponse: control.value,
          options: [],
          score: null,
        });
      } else if (!isTextInput && selectedOptions.length > 0) {
        const othersOption = this.getFollowUpOthersOption(fuq);
        const othersId = othersOption ? (othersOption.optionId || othersOption.text) : null;
        const othersSelected = othersId != null && selectedOptions.includes(othersId);
        const othersResponse = othersSelected ? (this.followUpOthersTextMap.get(questionId) || '') : undefined;
        responses.push({
          questionId: questionId,
          textResponse: null,
          options: selectedOptions.map(optId => ({
            optionId: optId,
            optionSelected: 'true'
          })),
          ...(othersResponse !== undefined && { othersResponse }),
          score: null,
        });
      }
    });
    
    return responses;
  }
}
